import React from "react";
import Nav from "../components/Nav";

function AboutPage() {
  return (
    <div className="about-page">
      <Nav />
      <div className="about-container">
        <h1 className="about-title">About This Project</h1>
        <section className="about-section">
          <h2>What is this?</h2>
          <p>
            The Unloaned Books in London Libraries project is an independent
            research project to discover the hidden opportunities in London's
            public library collections. It includes an interactive map of
            unloaned books in public library branches across London boroughs. It
            lets you explore the book collections and discover your next
            favourite book.
            <strong>
              {" "}
              This project is not affiliated with any library system or council,
              and is a purely independent research project.
            </strong>
          </p>
        </section>
        <section className="about-section">
          <h2>Where does the data come from?</h2>
          <p>
            The underlying data was collected through{" "}
            <strong>Freedom of Information (FOIA) requests</strong> submitted to
            London borough councils in the summer of 2025. Each council was
            asked to provide a list of physical books (ignoring e-books,
            audiobooks, etc.) on their shelves that have no record of being
            checked out by any reader, ever. Books must have been on the shelf
            for at least 1 year to be included. The data was then processed and
            enriched with additional information from public sources (primarily{" "}
            <a
              href="https://openlibrary.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Library
            </a>{" "}
            and{" "}
            <a
              href="https://developers.google.com/books"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Books
            </a>
            ).
          </p>
        </section>
        <section className="about-section">
          <h2>Data coverage</h2>
          <p>
            We received data from <strong>22 London boroughs</strong>, with the
            remaining 11 indicating they could not provide the data requested.
            The 22 councils together had a combined catalogue of approximately{" "}
            <strong>477,000 records</strong> across{" "}
            <strong>200 library branches</strong>.
          </p>
          <h3 className="about-subheading">Councils that provided data</h3>
          <div className="about-councils">
            {[
              "Barking and Dagenham",
              "Bexley",
              "Brent",
              "Bromley",
              "Camden",
              "City of London",
              "Croydon",
              "Greenwich",
              "Hackney",
              "Hammersmith and Fulham",
              "Haringey",
              "Havering",
              "Hillingdon",
              "Islington",
              "Kensington and Chelsea",
              "Lambeth",
              "Lewisham",
              "Newham",
              "Richmond upon Thames",
              "Southwark",
              "Tower Hamlets",
              "Westminster",
            ].map((c) => (
              <span key={c} className="council-tag">
                {c}
              </span>
            ))}
          </div>
          <h3 className="about-subheading">
            Councils that did not provide data
          </h3>
          <div className="about-councils">
            {[
              "Barnet",
              "Ealing",
              "Enfield",
              "Harrow",
              "Hounslow",
              "Kingston upon Thames",
              "Merton",
              "Redbridge",
              "Sutton",
              "Waltham Forest",
              "Wandsworth",
            ].map((c) => (
              <span key={c} className="council-tag council-tag--missing">
                {c}
              </span>
            ))}
          </div>
        </section>
        <section className="about-section">
          <h2>Limitations</h2>
          <ul className="about-list">
            <li>
              <strong>Not live.</strong> Catalogue snapshots were taken at a
              fixed point in time. The unloaned status of a book may have
              changed since then.
            </li>
            <li>
              <strong>Partial ISBN data.</strong> Not all books in the FOIA data
              have an ISBN. Some councils did not provide this information, or
              provided information that could not be matched to an external
              catalogue (likely an internal catalogue ID rather than a true
              ISBN). In some cases, books are old enough that they do not have
              an ISBN. For books that do not have an ISBN, we attempted to
              search for them in the Open Library API, and if found, we used the
              ISBN from the Open Library record. For books that still do not
              have an ISBN, we had to exclude them from this displayed dataset.
            </li>
            <li>
              <strong>Partial ratings.</strong> Only books whose ISBN could be
              matched to an external catalogue have ratings data. Most books
              show no ratings data.
            </li>
          </ul>
        </section>
        <section className="about-section">
          <h2>How can I get in contact?</h2>
          <p>
            You can get in contact with the project by emailing{" "}
            <a href="mailto:bongiovimatthew@gmail.com">
              bongiovimatthew@gmail.com
            </a>
            .
          </p>
        </section>

        <section className="about-section">
          <h2>How can I support the project?</h2>
          <p>
            You can support the project by donating to it on{" "}
            <a href="https://ko-fi.com/unloaned">Ko-fi</a>.
          </p>
        </section>

        <section className="about-section">
          <h2>FOIA request references</h2>
          <div className="foia-table-wrapper">
          <table className="foia-table">
            <thead>
              <tr>
                <th>Council</th>
                <th>FOIA Request</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Barking and Dagenham", "36906069"],
                ["Barnet", "14910923"],
                ["Bexley", "18209157"],
                ["Brent", "CAS-1269622-Y4Y8Q2"],
                ["Bromley", "DNLN4PQCN8"],
                ["Camden", "CAM10594"],
                ["City of London", "RFI WEF 15 July 2025"],
                ["Croydon", "12886"],
                ["Ealing", "0000962714"],
                ["Enfield", "101001637018"],
                ["Greenwich", "FOI-4629"],
                ["Hackney", "35731093"],
                ["Hammersmith and Fulham", "18277548"],
                ["Haringey", "FOI/816"],
                ["Harrow", "9499007"],
                ["Havering", "HAV-FOI-458118"],
                ["Hillingdon", "03465"],
                ["Hounslow", "1348235"],
                ["Islington", "6431716"],
                ["Kensington and Chelsea", "17001374"],
                ["Kingston upon Thames", "FOIS6546, FOIK6091"],
                ["Lambeth", "IRN37013461"],
                ["Lewisham", "32032633"],
                ["Merton", "AF145805746, F22433"],
                ["Newham", "FOI-2025-001510"],
                ["Redbridge", "29095062"],
                ["Richmond upon Thames", "FS-Case-736132118"],
                ["Southwark", "43346278"],
                ["Sutton", "FOIS6546, FOIK6091"],
                ["Tower Hamlets", "63101630"],
                ["Waltham Forest", "IR735880394"],
                ["Wandsworth", "FS-Case-735243053, LBR-FOI-10181"],
                ["Westminster", "41054897"],
              ].map(([council, ref]) => (
                <tr key={council}>
                  <td>{council}</td>
                  <td className="foia-ref">{ref}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </section>
      </div>
    </div>
  );
}

export default AboutPage;
